import {
  Toolbar,
  ToolbarButton,
  Input,
  type ToolbarButtonProps,
} from '@fluentui/react-components';
import { Search20Regular, Dismiss20Regular } from '@fluentui/react-icons';
import type { DataGridProps } from '@fluentui/react-components';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { enrichItemWithOtherInfo } from '@moko/utils';
import DataGridUI, {
  type DataGridColumn,
  type IRowMenuOption,
} from './DataGridUI';
import PaginationBar from './PaginationBar';
import useFetch from '../hooks/useFetch';
import { useObjectState } from '../hooks/useObjectState';
import { useSearchInput } from '../hooks/useSearchInput';
import {
  DataGridFilters,
  buildDefaultFilters,
  serializeFilters,
  type IActiveFilter,
  type IFilterableColumn,
} from './Filter';
import type { EmptyStateConfig } from '../Empty/Empty';
import { resolveCommandBarAppearance } from './commandBar';

// ============================================================================
// SearchInput
// ============================================================================

/**
 * Isolated search input. Owns its own typing state so per-keystroke
 * re-renders stay scoped to this component instead of the entire grid.
 * Auto-searches (debounced) once the term reaches the threshold, and
 * submits immediately on Enter — behavior governed by useSearchInput.
 */
interface SearchInputProps {
  onSubmit: (value: string) => void;
  onClear: () => void;
  disabled?: boolean;
  fullWidth?: boolean;
  threshold?: number;
}

const SearchInput = React.memo(function SearchInput({
  onSubmit,
  onClear,
  disabled,
  fullWidth,
  threshold,
}: SearchInputProps) {
  const { getInputProps, value, reset } = useSearchInput({
    onSearch: onSubmit,
    threshold,
  });
  const handleClear = useCallback(() => {
    reset('');
    onClear();
  }, [reset, onClear]);
  const inputProps = getInputProps();
  return (
    <Input
      placeholder="Search"
      style={fullWidth ? { width: '100%' } : undefined}
      value={inputProps.value}
      onChange={(e) => inputProps.onChange(e)}
      onKeyDown={inputProps.onKeyDown}
      contentBefore={<Search20Regular />}
      contentAfter={
        value ? (
          <Dismiss20Regular
            style={{ cursor: 'pointer' }}
            onClick={handleClear}
          />
        ) : undefined
      }
      disabled={disabled}
    />
  );
});

// ============================================================================
// Types
// ============================================================================

export interface IPagedCollection<T> {
  isLastPage: boolean;
  items: T[];
  pageCount: number;
  pageNo: number;
  pageSize: number;
  rowsCount: number;
}

/** Actions available to command bar items */
export interface ICommandBarContext {
  refresh: () => void;
}

export interface ICommandBarItem {
  key: string;
  text: string;
  icon?: React.JSX.Element;
  disabled?: boolean;
  appearance?: ToolbarButtonProps['appearance'];
  style?: React.CSSProperties;
  onClick?: (context: ICommandBarContext) => void;
}

export interface IPagedDataGridRefType {
  refresh: () => void;
  /** Remove one item from the persisted multi-selection (no-op in single-select). */
  deselectItem: (id: number) => void;
}

interface ApiParams {
  page: number;
  search: string;
  orderBy?: string;
  filters: IActiveFilter[];
  refreshCount: number;
}

/** Loading configuration */
export interface LoadingConfig {
  /** Loading message (default: "Loading ...") */
  message?: string;
  /** Icon element to display (default: CubeShapeIcon) */
  icon?: React.ReactNode;
}

export interface PagedDataGridProps<T> {
  /** Column definitions */
  columns: DataGridColumn<T>[];
  /** Data fetching function - receives all params, consumer decides which to use. Optional if items is provided. */
  getAction?: (
    abortController: AbortController,
    pageSize: number,
    pageNo: number,
    orderBy?: string,
    search?: string,
    filters?: string
  ) => Promise<IPagedCollection<T>>;
  /** Static items to display - when provided, getAction is not used and search/pagination/filters are hidden */
  items?: T[];
  /** Loading state for static items mode */
  loading?: boolean;
  /** Command bar items - each item receives context with actions like refresh */
  commandBarItems?: ICommandBarItem[];
  toolbarExtra?: React.ReactNode;
  /** Pagination bar items - each item receives context with actions like refresh */
  paginationBarItems?: ICommandBarItem[];
  /** Called when selection changes (single-select mode). For multiselect,
   * use `onMultiSelectionChange` instead. */
  onSelectionChange?: (selection: T) => void;
  /**
   * Controlled selected row id in single-select mode. Pass `null` for no
   * selection so the grid highlight stays in sync with the parent.
   */
  selectedId?: number | null;
  /**
   * Called when multi-selection changes. Receives the full set of selected
   * items, persisted across page changes (cleared on search/filter change).
   * Only meaningful when `selectionMode === 'multiselect'`.
   */
  onMultiSelectionChange?: (selection: T[]) => void;
  /** Called when row is double-clicked */
  onRowDoubleClicked?: (selection: T) => void;
  /** Called when loading starts */
  onLoading?: () => void;
  /** Called when loading ends */
  onLoaded?: () => void;
  /** Called on error */
  onError?: (error: Error) => void;
  /** Page size (default: 10) */
  pageSize?: number;
  /** Fixed height for the grid */
  listHeigh?: number;
  /** Selection mode */
  selectionMode?: DataGridProps['selectionMode'];
  /** Show drag handles on column edges (default: true). */
  resizableColumns?: boolean;
  /**
   * When true the table always fills 100% of its container. When false
   * (default) columns keep their declared widths and can be resized freely
   * past the container, scrolling horizontally.
   */
  fillContainer?: boolean;
  /** Disable search input */
  disabled?: boolean;
  /** Field names for parsing otherInfo */
  otherInfoFieldNames?: string[];
  /** Loading configuration (message and icon) */
  loadingConfig?: LoadingConfig;
  /** Show search input (default: true, ignored when items is provided) */
  searchable?: boolean;
  /**
   * Minimum characters before an auto-search fires (default: 1). Empty is
   * always a valid term (clears results). Below the threshold, typing/Enter
   * is a no-op. Set to 3 to require at least 3 characters.
   */
  searchThreshold?: number;
  /** Enable filtering (default: false, ignored when items is provided) */
  filterEnabled?: boolean;
  /**
   * Filter columns to expose in the "Add filter" picker. When provided this is
   * the single source of truth and any per-column `filter` config is ignored.
   * When omitted the picker falls back to deriving from `columns` (legacy).
   */
  filterColumns?: IFilterableColumn[];
  /**
   * When provided, clicking a row opens a context menu populated with these
   * options instead of selecting the row. Selection mode + double-click
   * navigation are implicitly disabled.
   */
  rowMenuOptions?: IRowMenuOption<T>[];
  empty?: EmptyStateConfig;
}

// ============================================================================
// Component
// ============================================================================

const PagedDataGrid = React.forwardRef(
  <T extends { id: number }>(
    props: PagedDataGridProps<T>,
    ref: React.ForwardedRef<IPagedDataGridRefType>
  ) => {
    const {
      columns,
      getAction,
      items: staticItems,
      loading: loadingProp,
      commandBarItems,
      toolbarExtra,
      paginationBarItems,
      onSelectionChange,
      selectedId,
      onMultiSelectionChange,
      onRowDoubleClicked,
      onLoading,
      onLoaded,
      onError,
      pageSize = 10,
      listHeigh,
      selectionMode,
      resizableColumns,
      fillContainer,
      disabled,
      otherInfoFieldNames,
      loadingConfig,
      searchable: searchableProp = true,
      searchThreshold = 1,
      filterEnabled: filterEnabledProp = false,
      filterColumns,
      rowMenuOptions,
      empty,
    } = props;

    // When static items are provided, disable search, pagination, and filters
    const isStaticMode = staticItems !== undefined;
    const searchable = !isStaticMode && searchableProp;
    const filterEnabled = !isStaticMode && filterEnabledProp;
    const showPagination = !isStaticMode;

    // ---- Derive filterable columns ----
    const filterableColumns = useMemo<IFilterableColumn[]>(() => {
      if (!filterEnabled) return [];
      if (filterColumns) return filterColumns;
      return columns
        .filter((col) => col.filter?.enabled)
        .map((col) => ({
          key: col.key,
          label: col.name ?? col.key,
          type: col.filter!.type,
          operators: col.filter!.operators,
          options: col.filter!.options,
          toggleOptions: col.filter!.toggleOptions,
        }));
    }, [columns, filterEnabled, filterColumns]);

    // ---- API Params State (useObjectState) ----
    const {
      state: params,
      updateProperty,
      bulkUpdate,
    } = useObjectState<ApiParams>({
      initialState: {
        page: 1,
        search: '',
        orderBy: undefined,
        filters: [],
        refreshCount: 0,
      },
    });

    const seededKeysRef = useRef<Set<string>>(new Set());
    useEffect(() => {
      if (!filterEnabled) return;
      const fresh = buildDefaultFilters(filterableColumns).filter(
        (f) => !seededKeysRef.current.has(f.column.key)
      );
      if (fresh.length === 0) return;
      fresh.forEach((f) => seededKeysRef.current.add(f.column.key));
      bulkUpdate({ filters: [...params.filters, ...fresh] });
    }, [filterEnabled, filterableColumns, params.filters, bulkUpdate]);

    // ---- Serialize filters for queryKey and API ----
    const serializedFilters = useMemo(
      () => (params.filters.length > 0 ? serializeFilters(params.filters) : ''),
      [params.filters]
    );

    // ---- Data Fetching (useFetch) - only when not in static mode ----
    const { data, loading: fetchLoading } = useFetch<IPagedCollection<T>>({
      queryKey: isStaticMode
        ? []
        : [
            params.page,
            params.search,
            params.orderBy,
            serializedFilters,
            params.refreshCount,
          ],
      queryFn: isStaticMode
        ? undefined
        : (abortController) =>
            getAction!(
              abortController,
              pageSize,
              params.page,
              params.orderBy,
              params.search,
              serializedFilters || undefined
            ),
      onLoading,
      onLoaded,
      onError,
      enabled: !isStaticMode,
    });

    // ---- Process items ----
    const items = useMemo(() => {
      const sourceItems = isStaticMode ? staticItems : data?.items;
      if (!sourceItems) return [];
      return sourceItems.map((item: any) =>
        otherInfoFieldNames
          ? enrichItemWithOtherInfo(item, otherInfoFieldNames)
          : item
      );
    }, [isStaticMode, staticItems, data?.items, otherInfoFieldNames]);

    const pageCount = data?.pageCount ?? 0;
    const isLastPage = data?.isLastPage ?? true;

    // Use prop loading for static mode, fetch loading otherwise
    const loading = isStaticMode ? loadingProp ?? false : fetchLoading;

    // ---- Refresh ----
    const refresh = useCallback(
      () => updateProperty('refreshCount', params.refreshCount + 1),
      [updateProperty, params.refreshCount]
    );

    // ---- Command Bar Context ----
    const commandBarContext = useMemo<ICommandBarContext>(
      () => ({ refresh }),
      [refresh]
    );

    // ---- Handlers ----
    const handleSearch = useCallback(
      (value: string) => {
        bulkUpdate({ page: 1, search: value });
      },
      [bulkUpdate]
    );

    const handleClearSearch = useCallback(() => {
      bulkUpdate({ page: 1, search: '' });
    }, [bulkUpdate]);

    const handleFilterApply = useCallback(
      (filter: IActiveFilter) => {
        bulkUpdate({ page: 1, filters: [...params.filters, filter] });
      },
      [params.filters, bulkUpdate]
    );

    const handleFilterUpdate = useCallback(
      (index: number, filter: IActiveFilter) => {
        const newFilters = [...params.filters];
        newFilters[index] = filter;
        bulkUpdate({ page: 1, filters: newFilters });
      },
      [params.filters, bulkUpdate]
    );

    const handleFilterRemove = useCallback(
      (index: number) => {
        const newFilters = params.filters.filter((_, i) => i !== index);
        bulkUpdate({ page: 1, filters: newFilters });
      },
      [params.filters, bulkUpdate]
    );

    const handleColumnSort = useCallback(
      (columnKey: string) => {
        const newOrderBy =
          params.orderBy === columnKey ? `${columnKey} Desc` : columnKey;
        updateProperty('orderBy', newOrderBy);
      },
      [params.orderBy, updateProperty]
    );

    const isMultiSelect = selectionMode === 'multiselect';
    const [selectedItemMap, setSelectedItemMap] = useState<Map<number, T>>(
      new Map()
    );

    const deselectItem = useCallback(
      (id: number) => {
        if (!isMultiSelect) return;
        setSelectedItemMap((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Map(prev);
          next.delete(id);
          onMultiSelectionChange?.(Array.from(next.values()));
          return next;
        });
      },
      [isMultiSelect, onMultiSelectionChange]
    );

    useImperativeHandle(ref, () => ({ refresh, deselectItem }), [
      refresh,
      deselectItem,
    ]);

    const handleSelectionChange = useCallback(
      (selectedItems: T[]) => {
        if (isMultiSelect) {
          // Reconcile the current page's checked rows against the persisted
          // map: keep off-page entries, replace on-page entries based on
          // what's currently checked in the grid.
          const visibleIds = new Set(items.map((it) => it.id));
          const visibleCheckedIds = new Set(selectedItems.map((it) => it.id));
          setSelectedItemMap((prev) => {
            const next = new Map<number, T>();
            prev.forEach((item, id) => {
              if (!visibleIds.has(id)) next.set(id, item);
            });
            selectedItems.forEach((it) => {
              if (visibleCheckedIds.has(it.id)) next.set(it.id, it);
            });
            onMultiSelectionChange?.(Array.from(next.values()));
            return next;
          });
          return;
        }
        if (selectedItems.length > 0) {
          onSelectionChange?.(selectedItems[0]);
        }
      },
      [isMultiSelect, items, onMultiSelectionChange, onSelectionChange]
    );

    // Subset of persisted ids visible on the current page — feeds Fluent's
    // checkbox state.
    const visibleSelectedIds = useMemo<Set<string | number>>(() => {
      if (!isMultiSelect) return new Set();
      const visible = new Set<string | number>();
      const itemIds = new Set(items.map((it) => it.id));
      selectedItemMap.forEach((_item, id) => {
        if (itemIds.has(id)) visible.add(id);
      });
      return visible;
    }, [isMultiSelect, items, selectedItemMap]);

    const handleRowDoubleClick = useCallback(
      (item: T) => {
        onRowDoubleClicked?.(item);
      },
      [onRowDoubleClicked]
    );

    // ---- Pagination Handlers ----
    const handleFirst = useCallback(
      () => updateProperty('page', 1),
      [updateProperty]
    );
    const handlePrev = useCallback(
      () => updateProperty('page', Math.max(1, params.page - 1)),
      [updateProperty, params.page]
    );
    const handleNext = useCallback(
      () => updateProperty('page', params.page + 1),
      [updateProperty, params.page]
    );
    const handleLast = useCallback(
      () => updateProperty('page', pageCount),
      [updateProperty, pageCount]
    );

    // ---- Render ----
    const hasCommandBarItems = (commandBarItems ?? []).length > 0;
    const hasFilterableColumns = filterableColumns.length > 0;
    const showToolbar =
      hasCommandBarItems ||
      searchable ||
      Boolean(toolbarExtra) ||
      (filterEnabled && hasFilterableColumns);

    const commandBar = showToolbar && (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        {searchable ? (
          <div style={{ flex: '0 1 320px', minWidth: 160 }}>
            <SearchInput
              onSubmit={handleSearch}
              onClear={handleClearSearch}
              disabled={disabled}
              fullWidth
              threshold={searchThreshold}
            />
          </div>
        ) : (
          <span />
        )}
        {toolbarExtra}
        {hasCommandBarItems && (
          <Toolbar
            aria-label="Command bar"
            size="small"
            style={{
              flex: '1 1 auto',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              gap: 16,
              padding: 0,
              minWidth: 0,
            }}
          >
            {commandBarItems?.map((item) => (
              <ToolbarButton
                key={item.key}
                aria-label={item.text}
                icon={item.icon}
                disabled={item.disabled}
                appearance={resolveCommandBarAppearance(
                  item.key,
                  item.appearance
                )}
                onClick={() => item.onClick?.(commandBarContext)}
                style={{
                  paddingLeft: 8,
                  paddingRight: 8,
                  paddingTop: 8,
                  paddingBottom: 8,
                  minWidth: 'auto',
                  ...item.style,
                }}
              >
                {item.text}
              </ToolbarButton>
            ))}
          </Toolbar>
        )}
      </div>
    );

    const filterBar = filterEnabled && hasFilterableColumns && (
      <div style={{ marginTop: 8, width: '100%' }}>
        <DataGridFilters
          columns={filterableColumns}
          filters={params.filters}
          onApply={handleFilterApply}
          onUpdate={handleFilterUpdate}
          onRemove={handleFilterRemove}
        />
      </div>
    );

    const hasPaginationBarItems = (paginationBarItems ?? []).length > 0;

    const paginationExtra = hasPaginationBarItems ? (
      <>
        {paginationBarItems!.map((item) => (
          <ToolbarButton
            key={item.key}
            aria-label={item.text}
            icon={item.icon}
            disabled={item.disabled}
            onClick={() => item.onClick?.(commandBarContext)}
          />
        ))}
      </>
    ) : null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {commandBar}
        {filterBar}
        <div style={{ marginTop: showToolbar ? 24 : 0 }}>
          <DataGridUI<T>
            items={items}
            columns={columns}
            loading={loading}
            loadingConfig={loadingConfig}
            height={listHeigh}
            selectionMode={selectionMode}
            selectedIds={
              isMultiSelect
                ? visibleSelectedIds
                : selectedId !== undefined
                ? selectedId != null
                  ? new Set<string | number>([selectedId])
                  : new Set<string | number>()
                : undefined
            }
            resizableColumns={resizableColumns}
            fillContainer={fillContainer}
            sortedColumn={params.orderBy}
            getRowId={(item) => item.id}
            onSelectionChange={handleSelectionChange}
            onRowDoubleClick={handleRowDoubleClick}
            onColumnSort={handleColumnSort}
            rowMenuOptions={rowMenuOptions}
            onRowMenuRefresh={refresh}
            empty={empty}
          />
        </div>
        {showPagination && items.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <PaginationBar
              page={params.page}
              pageCount={pageCount}
              isLastPage={isLastPage}
              onFirst={handleFirst}
              onPrev={handlePrev}
              onNext={handleNext}
              onLast={handleLast}
            >
              {paginationExtra}
            </PaginationBar>
          </div>
        )}
      </div>
    );
  }
) as <T extends { id: number }>(
  props: PagedDataGridProps<T> & {
    ref?: React.ForwardedRef<IPagedDataGridRefType>;
  }
) => React.ReactElement;

export default PagedDataGrid;
